import { useMemo } from "react";
import useQuery from "hooks/useQuery";
import useQueryStatusBuilder from "hooks/useQueryStatusBuilder";
import PersonalBestService from "services/PersonalBestService";
import { MetadataType } from "types";
import type { Status } from "types/Status";
import { SortType } from "types/output-exercise";

export interface PersonalBestResult {
  athleteId: string;
  exerciseId: string;
  metricField: string;
  value: number | null;
  date: string | null;
}

interface UseGetPersonalBestsQuery {
  data: PersonalBestResult[];
  status: Status;
}

interface UseGetPersonalBestQueryProps {
  athleteId: string;
  exerciseId: string;
  metricField: string;
  type: MetadataType;
  sortType: SortType;
}

/**
 * Groups params by exercise key to enable batch fetching.
 * Params with the same exerciseId, metricField, and sortType can be fetched together.
 */
function groupByExercise(params: UseGetPersonalBestQueryProps[]) {
  const groups = new Map<string, UseGetPersonalBestQueryProps[]>();

  params.forEach((param) => {
    const key = `${param.exerciseId}_${param.metricField}_${param.sortType}`;
    const existing = groups.get(key) || [];
    groups.set(key, [...existing, param]);
  });

  return groups;
}

/**
 * @description This hook retrieves personal bests for both:
 * - many athletes to 1 exercise (batched into single API call)
 * - many exercises to 1 athlete (one API call per exercise)
 */
function useGetManyPersonalBestQuery(params: UseGetPersonalBestQueryProps[], enabled = true): UseGetPersonalBestsQuery {
  const key = useMemo(() => {
    if (!enabled) return null;
    const filterMetadataExercisesOnly = params.filter((param) => param.type === MetadataType.EXERCISE);
    if (filterMetadataExercisesOnly.length === 0) return null;
    return ["PersonalBestService.getMany", filterMetadataExercisesOnly] as const;
  }, [params, enabled]);

  const { data, isLoading, error } = useQuery(key, async ([, queryParams]) => {
    const groups = groupByExercise(queryParams);

    const results = await Promise.all(
      Array.from(groups.entries()).map(async ([, groupParams]) => {
        const athleteIds = groupParams.map((p) => p.athleteId);
        const { exerciseId, metricField, sortType } = groupParams[0];

        const pbResults = await PersonalBestService.getMany(athleteIds, {
          exerciseId,
          metricId: metricField,
          sortType,
        });

        return groupParams.map((param) => {
          const pb = pbResults.find((r) => r.athleteId === param.athleteId);
          return {
            athleteId: param.athleteId,
            exerciseId: param.exerciseId,
            metricField: param.metricField,
            value: pb?.value ?? null,
            date: pb?.date ?? null,
          };
        });
      })
    );

    return results.flat();
  });

  const status = useQueryStatusBuilder({ isLoading, isError: !!error });

  return useMemo(() => ({ data: data || [], status }), [data, status]);
}

export default useGetManyPersonalBestQuery;
